<template>
  <SinglePageLayout :content-size="contentSize" :disable-container="disableContainer">
    <template #breadcrumb>
      <Breadcrumbs :hide-home-route="true" :items="breadcrumbsItems"></Breadcrumbs>
    </template>

    <template #titlebar>
      <div v-if="node != null" class="tw-flex tw-items-center tw-justify-between tw-gap-y-3">
        <div class="tw-flex-1">
          <AdvancedTitle
            v-model:is-editing="isEditingFqdn"
            :is-submitting-title="isSubmittingFqdn"
            :title="node.fqdn"
            editable
            pretitle="Node"
            @changed-title="changeFqdn"
          >
            <template #subtitle>
              <div>
                <NodeStatus :node="node" />
              </div>
            </template>
            <template #icon>
              <div class="tw-relative">
                <img
                  v-if="node.image && node.image.operatingSystem && node.image.operatingSystem.icon"
                  :alt="`${node.image.operatingSystem.name} icon`"
                  :src="toImageSrc(node.image.operatingSystem.icon)"
                  class="tw-rounded"
                  height="84"
                  width="84"
                />
                <FontAwesomeIcon
                  v-else-if="
                    node.image &&
                    node.image.operatingSystem &&
                    node.image.operatingSystem.family === OperatingSystemFamily.WINDOWS
                  "
                  :icon="['fab', 'windows']"
                  class="tw-text-[5.25rem]"
                />
                <FontAwesomeIcon v-else :icon="['fab', 'linux']" class="tw-text-[5.25rem]" />

                <GpTooltip
                  v-if="node.cloudProvider"
                  :content="`Cloud provider: ${node.cloudProvider.name} (${cloudProviderType})`"
                  class="tw-absolute -tw-bottom-2 -tw-right-2 tw-block"
                  trigger="hover"
                >
                  <CloudProviderBadge
                    :provider-type="node.cloudProvider.type"
                    class="tw-block"
                    hide-label
                  />
                </GpTooltip>
              </div>
            </template>

            <template #badge>
              <span
                v-if="node.isManaged"
                class="tw-block tw-h-5 tw-rounded tw-border tw-border-gp-ocean tw-bg-gp-ocean tw-px-2 tw-text-xs tw-font-bold tw-uppercase tw-leading-4 tw-text-gp-black"
                >Managed</span
              >
              <Tags :tags="node.tags" class="md:tw-justify-end" />
            </template>

            <template #actions>
              <GpMenu
                ref="actionsMenu"
                :icon="['fas', 'fa-ellipsis']"
                body-min-width="12rem"
                hide-indicator
                outline
                size="sm"
              >
                <GpMenuItemBtn :icon="['fas', 'fa-pen']" label="Rename" @click="toggleEditFqdn" />

                <GpMenuItemAction v-if="!node.isManaged" v-slot="{ contentClass }">
                  <router-link
                    v-slot="{ href, navigate }"
                    :to="{ name: 'ProjectNodeReinstall' }"
                    custom
                  >
                    <GpMenuItemContent
                      :class="contentClass"
                      :href="href"
                      :icon="['fas', 'fa-undo']"
                      component="a"
                      label="Reinstall"
                      @click="navigate"
                    />
                  </router-link>
                </GpMenuItemAction>

                <GpMenuItemDivider />

                <GpMenuItemBtn
                  v-if="!node.cloudProvider"
                  :disabled="!nodeActionsAllowed"
                  :icon="['fas', 'fa-wrench']"
                  label="Billing"
                  @click="showChangePeriodModal = true"
                />

                <template
                  v-if="node.billingPeriod === BillingPeriod.HOURLY || project?.canForceDeleteNodes"
                >
                  <GpMenuItemBtn
                    :disabled="!nodeActionsAllowed"
                    :icon="['fas', 'fa-trash']"
                    label="Delete"
                    @click="showDestroyModal = true"
                  />
                </template>
              </GpMenu>
            </template>
          </AdvancedTitle>
        </div>
      </div>

      <div v-if="loadNodeHasError" class="tw-mt-4 tw-flex tw-w-full tw-flex-col tw-items-center">
        <p class="tw-text-xl">
          Failed to load node <CommandText>{{ route.params.id }}</CommandText>
        </p>

        <router-link
          v-slot="{ href, navigate }"
          :to="{ name: 'ProjectNodes', params: { projectId: project.id } }"
          custom
        >
          <GpBtnLink
            :href="href"
            :icon="['fas', 'fa-arrow-left']"
            class="tw-mt-4"
            label="Go to project nodes"
            variant="primary"
            @click="navigate"
          />
        </router-link>
      </div>
    </template>

    <template #tabbar>
      <Tabbar v-if="node != null">
        <TabbarItem :to="{ name: 'ProjectNodeOverview' }" title="Overview" />
        <TabbarItem
          :disabled="!nodeActionsAllowed"
          :hover-title="!nodeActionsAllowed ? 'Node action disabled.' : ''"
          :to="{ name: 'ProjectNodeAccess' }"
          title="Access"
        />
        <TabbarItem
          :disabled="!nodeActionsAllowed"
          :hover-title="!nodeActionsAllowed ? 'Node action disabled.' : ''"
          :to="{ name: 'ProjectNodePower' }"
          title="Power"
        />
      </Tabbar>
    </template>

    <template #content>
      <main v-if="node != null">
        <DestroyNodeModal
          :show="showDestroyModal"
          :on-destroyed="confirmedNodeDestroy"
          @close="closeDestroyModal"
        />

        <ChangeComputeBillingPeriodModal
          :node="node"
          :show="showChangePeriodModal"
          @close="showChangePeriodModal = false"
          @confirm="showChangePeriodModal = false"
        />

        <slot name="content" />
      </main>
    </template>
  </SinglePageLayout>
</template>
<script lang="ts">
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpBtnLink,
  GpMenu,
  GpMenuItemAction,
  GpMenuItemBtn,
  GpMenuItemContent,
  GpMenuItemDivider,
  GpTooltip,
} from '@gportal/uikit';
import ChangeComputeBillingPeriodModal from '@panel/components/node/ChangeComputeBillingPeriodModal.vue';
import DestroyNodeModal from '@panel/components/node/DestroyNodeModal.vue';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import Size from 'gpcore-uikit/interfaces/Size';
import CloudProviderBadge from 'gpcore-uikit/components/CloudProviderBadge.vue';
import { toImageSrc } from 'gpcore-apikit/lib/fileHelper';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { OperatingSystemFamily } from 'gpcore-grpc/gpcore/api/cloud/v1/image_pb';
import { type Node, NodeSchema } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import type { BreadcrumbLinkItem } from 'gpcore-uikit/components/breadcrumbs/BreadcrumbLink.vue';
import Breadcrumbs from 'gpcore-uikit/components/breadcrumbs/Breadcrumbs.vue';
import AdvancedTitle from 'gpcore-uikit/components/Component.AdvancedTitle.vue';
import NodeStatus from 'gpcore-uikit/components/Component.Node.Status.vue';
import Tags from 'gpcore-uikit/components/Component.Node.Tags.vue';
import TabbarItem from 'gpcore-uikit/components/Component.Tabbar.Item.vue';
import Tabbar from 'gpcore-uikit/components/Component.Tabbar.vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { type PropType, computed, defineComponent, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import SinglePageLayout from './Layout.SinglePage.vue';
import getEnumLabel from '@lib/getEnumLabel';
import {
  CloudProviderType,
  CloudProviderTypeSchema,
} from 'gpcore-grpc/gpcore/type/v1/cloud_provider_pb';
import CommandText from 'gpcore-uikit/components/Component.Command.Text.vue';
import { create } from '@bufbuild/protobuf';

export default defineComponent({
  computed: {
    CloudProviderTypeSchema() {
      return CloudProviderTypeSchema;
    },
  },
  components: {
    Tags,
    Tabbar,
    TabbarItem,
    AdvancedTitle,
    Breadcrumbs,
    SinglePageLayout,
    CloudProviderBadge,
    NodeStatus,
    CommandText,
    FontAwesomeIcon,
    DestroyNodeModal,
    ChangeComputeBillingPeriodModal,
    GpMenu,
    GpMenuItemAction,
    GpMenuItemContent,
    GpMenuItemBtn,
    GpMenuItemDivider,
    GpTooltip,
    GpBtnLink,
  },
  props: {
    disableContainer: {
      type: Boolean,
      default: false,
    },
    contentSize: {
      type: Number as PropType<Size>,
      default: Size.Large,
    },
  },
  setup() {
    const { addNotification } = useNotifications();

    const nodeStore = useNodeStore();
    const node = computed(() => nodeStore.node);
    const nodeActionsAllowed = computed(() => nodeStore.nodeActionsAllowed);

    const getNode = (id: string) => nodeStore.getNode(id);
    const updateNode = (node: Node) => nodeStore.updateNode(node);

    const actionsMenu = ref<InstanceType<typeof GpMenu>>();
    const isEditingFqdn = ref<boolean>(false);
    const isSubmittingFqdn = ref<boolean>(false);
    const toggleEditFqdn = () => {
      isEditingFqdn.value = !isEditingFqdn.value;
      actionsMenu.value.hideMenuBody();
    };
    const changeFqdn = (fqdn: string) => {
      isSubmittingFqdn.value = true;

      const n = create(NodeSchema);
      n.id = node.value.id;
      n.fqdn = fqdn;
      n.tags = node.value.tags;

      updateNode(n)
        .then(() => {
          addNotification({
            message: 'The node hostname has been changed.',
            type: 'success',
          });
        })
        .catch((error: ServiceError) => {
          addNotification({
            message: error.message,
            type: 'danger',
          });
        })
        .finally(() => {
          isSubmittingFqdn.value = false;
          isEditingFqdn.value = false;
        });
    };

    const projectStore = useProjectStore();
    const project = computed(() => projectStore.project);

    const showDestroyModal = ref<boolean>(false);
    const showRenewModal = ref<boolean>(false);
    const showChangePeriodModal = ref<boolean>(false);

    const closeDestroyModal = () => {
      showDestroyModal.value = false;
    };

    const router = useRouter();
    const route = useRoute();
    const confirmedNodeDestroy = async (): Promise<void> => {
      await router.push({ name: 'ProjectNodes', params: { projectId: project.value.id } });
    };

    const loadNodeHasError = ref<boolean>(false);
    onMounted(async () => {
      try {
        await getNode(route.params.id as string);
      } catch (error) {
        loadNodeHasError.value = true;
        addNotification({
          message: (error as ServiceError).message,
          type: 'danger',
        });
      }
    });

    const currentRouteName = computed(() => route.name);
    const breadcrumbsItems = computed(() => {
      const items: Array<BreadcrumbLinkItem | Array<BreadcrumbLinkItem>> = [
        {
          label: 'Project nodes',
          to: {
            name: 'ProjectNodes',
            params: {
              projectId: project.value.id,
            },
          },
        },
      ];

      if (currentRouteName.value === 'ProjectNodeReinstall') {
        items.push({ label: 'Overview', to: { name: 'ProjectNodeOverview' } });
        items.push({ label: 'Reinstall', to: { name: 'ProjectNodeReinstall' } });
      } else {
        items.push([
          { label: 'Overview', to: { name: 'ProjectNodeOverview' } },
          { label: 'Access', to: { name: 'ProjectNodeAccess' } },
          { label: 'Power', to: { name: 'ProjectNodePower' } },
        ]);
      }

      return items;
    });

    const cloudProviderType = computed(() => {
      if (node.value?.cloudProvider) {
        return getEnumLabel(CloudProviderType, node.value.cloudProvider.type);
      }

      return '';
    });

    return {
      node,
      route,
      loadNodeHasError,
      cloudProviderType,
      nodeActionsAllowed,
      toImageSrc,
      getNode,
      updateNode,
      toggleEditFqdn,
      isEditingFqdn,
      isSubmittingFqdn,
      changeFqdn,
      OperatingSystemFamily,
      project,

      actionsMenu,

      showDestroyModal,
      showRenewModal,
      showChangePeriodModal,
      closeDestroyModal,
      confirmedNodeDestroy,

      BillingPeriod,

      breadcrumbsItems,
    };
  },
});
</script>
